// Threshold sizing: with the quote auto-refreshing every 30s, a compliant
// route stays within a fraction of a percent — anything past 1% worse is
// worth a re-confirm.
const MAX_ACCEPTED_DRIFT_BPS = 100n;

// Consent guard for swap execution-time rate updates (wired into LiFi's
// acceptExchangeRateUpdateHook): whether the execution-time receive amount
// may silently replace the displayed quote, or the swap step must abort so
// the user re-confirms at a fresh quote. An unconditional `true` here is
// the failure mode — an idle-stale quote executing at any rate with no
// re-consent.
//
// Both amounts are wei-style integer strings compared as BigInt — no
// precision loss at 256-bit scale. An equal-or-better rate is always
// accepted; a worse one is accepted down to exactly 1% below the old amount
// (100 bps, boundary inclusive) and rejected past that. An amount BigInt
// can't parse (fractional, non-numeric) → false — refuse rather than
// blind-accept. Degenerate non-positive old amount → plain `new >= old`,
// so a zero-quote never blocks a non-negative execution amount.
export function isRateDriftAcceptable(
  oldToAmount: string,
  newToAmount: string,
): boolean {
  try {
    const oldAmt = BigInt(oldToAmount);
    const newAmt = BigInt(newToAmount);
    // Stryker disable next-line EqualityOperator: equivalent mutant — at oldAmt === 0n the band formula reduces to newAmt * 10_000n >= 0n, i.e. newAmt >= 0n, exactly this guard's newAmt >= oldAmt, so <= vs < is indistinguishable for all inputs
    if (oldAmt <= 0n) {
      return newAmt >= oldAmt;
    }
    return newAmt * 10_000n >= oldAmt * (10_000n - MAX_ACCEPTED_DRIFT_BPS);
  } catch {
    return false;
  }
}
